(function heroSlider() {
  const STATE_CLASSES = [
    "left-2",
    "left-1",
    "center",
    "right-1",
    "right-2",
    "hidden",
    "exit-left",
    "teleport-right",
    "no-transition",
  ];
  const ANIMATION_MS = 800;
  const WRAP_EXIT_MS = 360;
  const AUTOPLAY_MS = 3500;
  const sliderControllers = new WeakMap();

  function getRoleForOffset(offset, totalCards) {
    if (totalCards <= 2) {
      if (offset === 0) return "center";
      return "hidden";
    }

    if (offset === 0) return "center";
    if (offset === 1) return "right-1";
    if (offset === 2) return "right-2";
    if (offset === totalCards - 1) return "left-1";
    if (offset === totalCards - 2) return "left-2";

    return "hidden";
  }

  function initSlider(sliderRoot) {
    const cards = Array.from(sliderRoot.querySelectorAll(".carousel-card"));
    if (!cards.length) return null;

    let currentIndex = 0;
    let isAnimating = false;
    let isPaused = false;
    let autoplayTimerId = null;
    let revealTimerId = null;
    let cleanupFns = [];

    const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    function clearTimer() {
      if (!autoplayTimerId) return;
      window.clearTimeout(autoplayTimerId);
      autoplayTimerId = null;
    }

    function clearRevealTimer() {
      if (!revealTimerId) return;
      window.clearTimeout(revealTimerId);
      revealTimerId = null;
    }

    function removeStateClasses(card) {
      card.classList.remove(...STATE_CLASSES);
    }

    function getCardRole(card) {
      const roleClass = ["left-2", "left-1", "center", "right-1", "right-2", "hidden"].find((role) =>
        card.classList.contains(role)
      );

      if (roleClass) return roleClass;
      return "hidden";
    }

    function shouldWrapFromLeft(roleFrom, roleTo) {
      if (cards.length !== 5) return false;
      if (roleFrom === "left-2") {
        if (roleTo === "right-2") return true;
      }
      return false;
    }

    function applyRoles(indexToApply, options) {
      const roleMap = new Map();
      const wrapCards = [];

      cards.forEach((card, index) => {
        const offset = (index - indexToApply + cards.length) % cards.length;
        const nextRole = getRoleForOffset(offset, cards.length);
        roleMap.set(card, nextRole);
      });

      cards.forEach((card) => {
        const currentRole = getCardRole(card);
        const nextRole = roleMap.get(card);

        if (!options.force) {
          if (shouldWrapFromLeft(currentRole, nextRole)) {
            wrapCards.push(card);
            return;
          }
        }

        removeStateClasses(card);
        card.classList.add(nextRole);
      });

      wrapCards.forEach((card) => {
        removeStateClasses(card);
        card.classList.add("exit-left");
      });

      if (!wrapCards.length) return;

      sliderRoot.classList.add("is-wrapping");

      window.setTimeout(() => {
        wrapCards.forEach((card) => {
          removeStateClasses(card);
          card.classList.add("no-transition", "teleport-right");
        });

        wrapCards.forEach((card) => {
          card.getBoundingClientRect();
          card.classList.remove("no-transition", "teleport-right");
          card.classList.add("right-2");
        });

        sliderRoot.classList.remove("is-wrapping");
      }, WRAP_EXIT_MS);
    }

    function updateCarousel(newIndex, force) {
      if (isAnimating) return;

      const normalizedIndex = (newIndex + cards.length) % cards.length;
      if (!force && normalizedIndex === currentIndex) return;

      isAnimating = true;
      currentIndex = normalizedIndex;

      applyRoles(currentIndex, { force });

      window.setTimeout(() => {
        isAnimating = false;
      }, ANIMATION_MS);
    }

    function tick() {
      clearTimer();

      if (isPaused || document.hidden || reducedMotionQuery.matches || cards.length <= 1) {
        autoplayTimerId = window.setTimeout(tick, AUTOPLAY_MS);
        return;
      }

      updateCarousel(currentIndex + 1, false);
      autoplayTimerId = window.setTimeout(tick, AUTOPLAY_MS);
    }

    function onPointerEnter() {
      isPaused = true;
    }

    function onPointerLeave() {
      isPaused = false;
    }

    function onVisibilityChange() {
      if (document.hidden) {
        isPaused = true;
        return;
      }

      isPaused = false;
    }

    function onMotionPreferenceChange() {
      updateCarousel(currentIndex, true);

      if (reducedMotionQuery.matches) {
        sliderRoot.classList.remove("is-revealing");
      }
    }

    sliderRoot.addEventListener("pointerenter", onPointerEnter);
    sliderRoot.addEventListener("pointerleave", onPointerLeave);
    document.addEventListener("visibilitychange", onVisibilityChange);
    reducedMotionQuery.addEventListener("change", onMotionPreferenceChange);

    cleanupFns = [
      () => sliderRoot.removeEventListener("pointerenter", onPointerEnter),
      () => sliderRoot.removeEventListener("pointerleave", onPointerLeave),
      () => document.removeEventListener("visibilitychange", onVisibilityChange),
      () => reducedMotionQuery.removeEventListener("change", onMotionPreferenceChange),
    ];

    updateCarousel(0, true);

    window.requestAnimationFrame(() => {
      sliderRoot.classList.remove("is-initializing");
      sliderRoot.classList.add("is-ready");

      if (reducedMotionQuery.matches) return;

      sliderRoot.classList.add("is-revealing");
      clearRevealTimer();
      revealTimerId = window.setTimeout(() => {
        sliderRoot.classList.remove("is-revealing");
        revealTimerId = null;
      }, 520);
    });

    tick();

    return () => {
      clearTimer();
      clearRevealTimer();
      cleanupFns.forEach((cleanup) => cleanup());
      cleanupFns = [];
      sliderRoot.dataset.sliderInitialized = "false";
    };
  }

  function initAll(scope) {
    const root = scope || document;
    const sliders = root.querySelectorAll("[data-hero-slider]");

    sliders.forEach((slider) => {
      if (slider.dataset.sliderInitialized === "true") return;

      const destroyController = initSlider(slider);
      if (!destroyController) return;

      slider.dataset.sliderInitialized = "true";
      sliderControllers.set(slider, destroyController);
    });
  }

  function destroyAll(scope) {
    const root = scope || document;
    const sliders = root.querySelectorAll("[data-hero-slider]");

    sliders.forEach((slider) => {
      const destroyController = sliderControllers.get(slider);
      if (!destroyController) return;

      destroyController();
      sliderControllers.delete(slider);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyAll(event.target);
  });
})();
